// Admin — Reviews Management. Filter by product/vendor/rating, edit stars/comment, hide/unhide, delete.
import React, { useCallback, useEffect, useState } from 'react';
import { toast, Toaster } from 'sonner';
import { RefreshCw, Star, ShieldCheck, EyeOff, Trash2, Edit3, X } from 'lucide-react';
import api from '../../utils/api';

export default function AdminMarketplaceReviews() {
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({});
  const [busy, setBusy] = useState(false);
  const [filters, setFilters] = useState({ listing_id: '', vendor_id: '', stars: '', verified_only: false, hidden: '' });
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const params = new URLSearchParams();
      if (filters.listing_id) params.set('listing_id', filters.listing_id);
      if (filters.vendor_id)  params.set('vendor_id', filters.vendor_id);
      if (filters.stars)      params.set('stars', filters.stars);
      if (filters.verified_only) params.set('verified_only', 'true');
      if (filters.hidden !== '') params.set('hidden', filters.hidden);
      params.set('limit', '300');
      const r = await api.get(`/admin/marketplace/reviews?${params.toString()}`);
      setRows(r.data.reviews || []);
      setSummary({ total: r.data.total, hidden: r.data.hidden, verified: r.data.verified, shown: r.data.count });
    } catch (e) { toast.error(e?.response?.data?.detail || 'Load failed'); }
    finally { setBusy(false); }
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  const toggleHidden = async (r) => {
    try {
      await api.post(`/admin/marketplace/review/${r.id}`, { hidden: !r.hidden });
      toast.success(r.hidden ? 'Unhidden' : 'Hidden');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  const del = async (r) => {
    if (!window.confirm(`Delete review by ${r.buyer_name} (${r.stars}★)? Cannot undo.`)) return;
    try {
      await api.delete(`/admin/marketplace/review/${r.id}`);
      toast.success('Deleted'); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-purple-950 to-slate-950 text-white p-4 sm:p-6" data-testid="admin-reviews-page">
      <Toaster position="top-right" richColors />
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-black bg-gradient-to-r from-yellow-300 to-orange-400 bg-clip-text text-transparent flex items-center gap-2">
              <Star size={28} className="fill-yellow-300" /> Reviews Management
            </h1>
            <div className="text-[11px] opacity-70">
              Total: <b className="text-yellow-300">{summary.total || 0}</b> · Verified: <b className="text-emerald-300">{summary.verified || 0}</b> · Hidden: <b className="text-rose-300">{summary.hidden || 0}</b> · Showing: <b>{summary.shown || 0}</b>
            </div>
          </div>
          <button onClick={load} disabled={busy} data-testid="admin-reviews-refresh"
                  className="px-3 py-1.5 rounded-lg bg-purple-500 text-white text-xs font-black flex items-center gap-1">
            <RefreshCw size={12} className={busy ? 'animate-spin' : ''} /> Refresh
          </button>
        </div>

        {/* Filters */}
        <div className="rounded-2xl bg-slate-900/60 border border-slate-800 p-3 mb-3 grid grid-cols-2 sm:grid-cols-5 gap-2" data-testid="admin-reviews-filters">
          <input value={filters.listing_id} onChange={(e) => setFilters({ ...filters, listing_id: e.target.value })}
                 placeholder="Listing ID" data-testid="filter-listing-id"
                 className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
          <input value={filters.vendor_id} onChange={(e) => setFilters({ ...filters, vendor_id: e.target.value })}
                 placeholder="Vendor ID" data-testid="filter-vendor-id"
                 className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
          <select value={filters.stars} onChange={(e) => setFilters({ ...filters, stars: e.target.value })}
                  data-testid="filter-stars"
                  className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs">
            <option value="" className="bg-slate-900">All ratings</option>
            {[5, 4, 3, 2, 1].map((n) => <option key={n} value={n} className="bg-slate-900">{n}★ only</option>)}
          </select>
          <select value={filters.hidden} onChange={(e) => setFilters({ ...filters, hidden: e.target.value })}
                  data-testid="filter-hidden"
                  className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs">
            <option value="" className="bg-slate-900">All visibility</option>
            <option value="false" className="bg-slate-900">Visible only</option>
            <option value="true" className="bg-slate-900">Hidden only</option>
          </select>
          <label className="flex items-center gap-1 text-[10px] font-black text-emerald-300">
            <input type="checkbox" checked={filters.verified_only}
                   onChange={(e) => setFilters({ ...filters, verified_only: e.target.checked })}
                   data-testid="filter-verified" />
            ✔️ Verified only
          </label>
        </div>

        {/* Reviews list */}
        <div className="rounded-2xl bg-slate-900/70 border border-slate-800 overflow-hidden" data-testid="admin-reviews-list">
          {rows.length === 0 && (
            <div className="p-12 text-center opacity-60 text-sm">
              {busy ? '⏳ Loading…' : 'No reviews match filters'}
            </div>
          )}
          <div className="divide-y divide-slate-800">
            {rows.map((r) => (
              <div key={r.id} className={`p-3 flex items-start gap-3 flex-wrap hover:bg-slate-800/40 ${r.hidden ? 'opacity-50' : ''}`}
                   data-testid={`admin-review-${r.id.slice(0,8)}`}>
                <div className="w-9 h-9 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-xs font-black">
                  {(r.buyer_name || '?')[0]?.toUpperCase()}
                </div>
                <div className="flex-1 min-w-[180px]">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-black">{r.buyer_name || 'Anonymous'}</span>
                    {r.verified_purchase && (
                      <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-emerald-500/25 text-emerald-300 border border-emerald-400/60 font-black flex items-center gap-0.5">
                        <ShieldCheck size={9} /> Verified
                      </span>
                    )}
                    {r.hidden && (
                      <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-rose-500/25 text-rose-300 border border-rose-400/60 font-black">HIDDEN</span>
                    )}
                    <span className="flex items-center gap-0.5 ml-1">
                      {[1,2,3,4,5].map((n) => (
                        <Star key={n} size={11} className={r.stars >= n ? 'text-yellow-300 fill-yellow-300' : 'text-slate-600'} />
                      ))}
                    </span>
                  </div>
                  <div className="text-[10px] opacity-70 mt-0.5">
                    📦 <b>{r.listing_title || r.listing_id?.slice(0, 8)}</b> · 🏪 {r.shop_slug || '—'} · {new Date(r.created_at).toLocaleString('en-IN')}
                  </div>
                  {r.comment && <div className="text-[11px] mt-1 opacity-90">{r.comment}</div>}
                  {r.vendor_reply && (
                    <div className="mt-1 pl-2 border-l-2 border-cyan-400 text-[10px] opacity-80">
                      <b className="text-cyan-300">Vendor:</b> {r.vendor_reply.text}
                    </div>
                  )}
                </div>
                <div className="flex gap-1">
                  <button onClick={() => setEditing(r)} data-testid={`admin-review-${r.id.slice(0,8)}-edit`}
                          className="p-2 rounded bg-slate-700 hover:bg-slate-600" title="Edit"><Edit3 size={13} /></button>
                  <button onClick={() => toggleHidden(r)} data-testid={`admin-review-${r.id.slice(0,8)}-hide`}
                          className={`p-2 rounded ${r.hidden ? 'bg-emerald-600' : 'bg-amber-600'}`} title={r.hidden ? 'Unhide' : 'Hide'}>
                    <EyeOff size={13} />
                  </button>
                  <button onClick={() => del(r)} data-testid={`admin-review-${r.id.slice(0,8)}-delete`}
                          className="p-2 rounded bg-rose-600 hover:bg-rose-500" title="Delete"><Trash2 size={13} /></button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {editing && <EditModal review={editing} onClose={() => setEditing(null)} onDone={() => { setEditing(null); load(); }} />}
      </div>
    </div>
  );
}


function EditModal({ review, onClose, onDone }) {
  const [stars, setStars] = useState(review.stars);
  const [comment, setComment] = useState(review.comment || '');
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/marketplace/review/${review.id}`, { stars, comment });
      toast.success('Updated'); onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-3" onClick={onClose} data-testid="admin-review-edit-modal">
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-md rounded-2xl bg-slate-900 border-2 border-yellow-400/60 p-4 space-y-2">
        <div className="flex items-center justify-between">
          <div className="text-sm font-black">Admin Edit Review</div>
          <button onClick={onClose}><X size={16} /></button>
        </div>
        <div className="text-[10px] opacity-70">by {review.buyer_name} · {review.listing_title}</div>
        <div className="flex items-center gap-1">
          {[1,2,3,4,5].map((n) => (
            <button key={n} onClick={() => setStars(n)} data-testid={`admin-edit-star-${n}`}
                    className="p-0.5 hover:scale-125 transition">
              <Star size={22} className={stars >= n ? 'text-yellow-300 fill-yellow-300' : 'text-slate-600'} />
            </button>
          ))}
        </div>
        <textarea value={comment} onChange={(e) => setComment(e.target.value)} rows={3}
                  data-testid="admin-edit-comment"
                  className="w-full px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
        <div className="flex gap-2">
          <button onClick={onClose} className="flex-1 py-2 rounded bg-slate-700 text-xs font-black">Cancel</button>
          <button onClick={submit} disabled={busy} data-testid="admin-edit-save"
                  className="flex-1 py-2 rounded bg-yellow-400 text-slate-900 text-xs font-black disabled:opacity-40">Save</button>
        </div>
      </div>
    </div>
  );
}
